/**
 * AI money suggestions: the local model reads a summary of your cards and bank accounts (what
 * you've entered on the Money page, never anything from your groups' people) and suggests
 * moves, like paying a card before it closes or moving idle cash somewhere that earns more.
 * Suggestions only; nothing moves money.
 */
import type { CardStatus, MoneyAccount } from "../money/cards.ts";

export const MONEY_SYSTEM = `You are a careful personal-finance helper for a college student in California.
You get a list of their accounts: credit cards (balance, limit, statement close and due dates) and bank or brokerage accounts (balance, notes).
Suggest at most 5 concrete, practical moves, most valuable first. Good topics: paying a card in full before the due date, keeping reported utilization low by paying before the statement closes, idle cash in checking that could sit in a higher-yield money market or core position (mention tax treatment only if the notes make it clear, e.g. municipal or Treasury funds in California), keeping a debit card linked only to a small account, and avoiding interest.
Be specific to the numbers. Don't invent accounts or rates you weren't given; say "check the current rate" instead. This isn't financial advice.
Reply with JSON only: [{"title":"short action","why":"one or two sentences","urgency":"now"|"soon"|"idea"}]`;

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export function moneyPrompt(
  rows: readonly { account: MoneyAccount; status?: CardStatus }[],
  today: string,
): string {
  const lines = [`Today is ${today}.`];
  for (const { account: a, status: s } of rows) {
    if (a.archived) continue;
    if (a.kind === "credit") {
      lines.push(
        `Credit card "${a.name}"${a.issuer ? ` (${a.issuer})` : ""}: owes ${s ? money(Math.round(s.owedMicro / 10_000)) : money(a.balanceCents)}` +
          `${a.limitCents ? ` of ${money(a.limitCents)} limit` : ""}` +
          `${s?.nextClose ? `, statement closes ${s.nextClose}` : ""}${s?.nextDue ? `, payment due ${s.nextDue}` : ""}` +
          `${a.note ? `. Notes: ${a.note}` : ""}`,
      );
    } else {
      lines.push(
        `${a.kind === "savings" ? "Savings/brokerage" : "Checking"} account "${a.name}"${a.issuer ? ` at ${a.issuer}` : ""}: balance ${money(a.balanceCents)} as of ${a.balanceOn}${a.note ? `. Notes: ${a.note}` : ""}`,
      );
    }
  }
  return lines.join("\n");
}

export interface MoneyAdvice {
  title: string;
  why: string;
  urgency: "now" | "soon" | "idea";
}

export function parseMoneyAdvice(text: string): MoneyAdvice[] {
  const start = text.indexOf("[");
  const end = text.lastIndexOf("]");
  if (start < 0 || end <= start) return [];
  let rows: unknown;
  try {
    rows = JSON.parse(text.slice(start, end + 1));
  } catch {
    return [];
  }
  if (!Array.isArray(rows)) return [];
  const out: MoneyAdvice[] = [];
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    const row = r as Record<string, unknown>;
    const title = typeof row.title === "string" ? row.title.trim().slice(0, 140) : "";
    if (!title) continue;
    const why = typeof row.why === "string" ? row.why.trim().slice(0, 600) : "";
    const urgency = row.urgency === "now" || row.urgency === "soon" ? row.urgency : "idea";
    out.push({ title, why, urgency });
  }
  return out.slice(0, 5);
}
